import { Component, ViewChild, ElementRef, Input, Output, EventEmitter} from '@angular/core';
import { ClientServiceService } from '../services/client-service.service';
import { FormDataService } from '../services/form-data.service';
import { firstValueFrom } from 'rxjs';
import jsPDF from 'jspdf';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { notoSansJPRegular } from '../fonts/noto-sans-jp.font';
import { CountryService } from '../services/country.service';
import { Country } from '../models/country.model';

@Component({
  selector: 'app-agreement-signature',
  templateUrl: './agreement-signature.component.html',
  styleUrls: ['./agreement-signature.component.css']
})
export class AgreementSignatureComponent {
  @Input() personalInfo: any = {};
  @Output() submit = new EventEmitter<void>();
  @Output() rerender = new EventEmitter<void>();
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('textContainer', { static: false }) textContainer?: ElementRef<HTMLDivElement>;
  private ctx!: CanvasRenderingContext2D | null;
  private isDrawing = false;

  private canvasContent: string | null = null;

  isSaved = false;

  isPhoneVisible: boolean = false;

  isLoaderActive: boolean = false;
  isCanvasHidden: boolean = false;

  new_form_email: string = "";

  auth_client_token: any = null;

  countries: Country[] = [];

  translated_country: string = "";
  translated_region: string = "";
  // currentDate = new Date().toLocaleDateString('en-US', {
  //   year: 'numeric',
  //   month: 'long',
  //   day: 'numeric'
  // });

  constructor(
    private formDataService: FormDataService,
    private clientService: ClientServiceService,
    private translate: TranslateService,
    private countryService: CountryService,
    private router: Router
  ) {}

  ngOnInit() {
    this.auth_client_token = localStorage.getItem("auth_client_token");
    if(!this.auth_client_token) {
      this.router.navigate(["/login"]);
    }
    window.addEventListener('resize', this.updateCanvasSize.bind(this));
    this.countryService.getCountries().subscribe({
      next: (data) => {
        this.countries = data;
        let { country = 'N/A', state_region = 'N/A' } = this.personalInfo;
        const selectedCountry = this.countries.find(
          (c) => c.countryName === country
        );
        this.translated_country = this.translate.instant(`countries.${selectedCountry?.countryShortCode}`);
        this.translated_region = this.translate.instant(`regions.${selectedCountry?.countryShortCode}.${state_region}`);
      },
      error: (err) => {
        // console.error(err);
      }
    });
  }

  ngAfterViewInit() {
    this.setupCanvas();
  }

  get currentDate(): string {
    const lang = this.translate.currentLang || 'en-US';
  
    const locales: { [key: string]: string } = {
      en: 'en-US',
      de: 'de-DE',
      ja: 'ja-JP'
    };
  
    const locale = locales[lang];
  
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
  
    return new Date().toLocaleDateString(locale, options);
  }

  get phoneNumber() {
    if (this.isPhoneVisible) {
      return this.personalInfo.phone;
    } 
    return this.maskPhoneNumber(this.personalInfo.phone);
  }

  toggleShowPhone () {
    this.isPhoneVisible = !this.isPhoneVisible;
  }

  maskPhoneNumber(phone: string): string {
    if (phone.length < 3) return phone;
    if (phone.length === 3) return phone.slice(0, 2) + '*';
    if (phone.length <= 6) return phone.slice(0, 2) + '*'.repeat(phone.length - 2);
    const first = phone.slice(0, 2);
    const last = phone.slice(6);
    const masked = '****';
    return first + masked + last;
  }

  private setupCanvas() {
    const canvasEl = this.canvas.nativeElement;
    this.ctx = canvasEl.getContext('2d');

    if (!this.ctx) {
      return;
    }

    const textContainerWidth = this.textContainer?.nativeElement.offsetWidth || 600;
    const maxWidth = 800;
    const minWidth = 280;
    const canvasWidth = Math.max(minWidth, Math.min(maxWidth, textContainerWidth));
    const canvasHeight = canvasWidth * 0.4;
    const dpr = window.devicePixelRatio || 1;

    if (this.checkSignature()) {
      this.canvasContent = canvasEl.toDataURL('image/png');
    }

    canvasEl.width = canvasWidth * dpr;
    canvasEl.height = canvasHeight * dpr;
    canvasEl.style.width = `${canvasWidth}px`;
    canvasEl.style.height = `${canvasHeight}px`;

    this.ctx.scale(dpr, dpr);
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.strokeStyle = '#000';

    if (this.canvasContent) {
      const img = new Image();
      img.src = this.canvasContent;
      img.onload = () => {
        this.ctx?.drawImage(img, 0, 0, canvasEl.width / dpr, canvasEl.height / dpr);
      };
    }
  }

  private updateCanvasSize() {
    if (!this.ctx) return;
    this.setupCanvas();
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    if (!this.ctx) return;

    this.isDrawing = true;
    event.preventDefault();

    const canvasEl = this.canvas.nativeElement;
    const rect = canvasEl.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    let x: number, y: number;
    if (event instanceof MouseEvent) {
      x = (event.clientX - rect.left) * (canvasEl.width / rect.width / dpr);
      y = (event.clientY - rect.top) * (canvasEl.height / rect.height / dpr);
    } else if (typeof window.TouchEvent !== 'undefined' && event instanceof TouchEvent) {
      const touch = event.touches[0];
      x = (touch.clientX - rect.left) * (canvasEl.width / rect.width / dpr);
      y = (touch.clientY - rect.top) * (canvasEl.height / rect.height / dpr);
    } else {
      return;
    }

    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing || !this.ctx) return;

    event.preventDefault();

    const canvasEl = this.canvas.nativeElement;
    const rect = canvasEl.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    let x: number, y: number;
    if (event instanceof MouseEvent) {
      x = (event.clientX - rect.left) * (canvasEl.width / rect.width / dpr);
      y = (event.clientY - rect.top) * (canvasEl.height / rect.height / dpr);
    } else if (typeof window.TouchEvent !== 'undefined' && event instanceof TouchEvent) {
      const touch = event.touches[0];
      x = (touch.clientX - rect.left) * (canvasEl.width / rect.width / dpr);
      y = (touch.clientY - rect.top) * (canvasEl.height / rect.height / dpr);
    } else {
      return;
    }

    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
  }

  stopDrawing() {
    if (this.isDrawing && this.ctx) {
      this.isDrawing = false;
      this.ctx.beginPath();

      this.canvasContent = this.canvas.nativeElement.toDataURL('image/png');
    }
  }

  checkSignature(): boolean {
    if (!this.ctx) return false;

    const canvasEl = this.canvas.nativeElement;
    const imageData = this.ctx.getImageData(0, 0, canvasEl.width, canvasEl.height).data;
    for (let i = 3; i < imageData.length; i += 4) {
      if (imageData[i] !== 0) {
        return true;
      }
    }
    return false;
  }

  clearSignature() {
    if (this.ctx) {
      const canvasEl = this.canvas.nativeElement;
      this.ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      this.canvasContent = null;
    }
  }

  async saveSignature() {
    if (!this.ctx) return;

    const canvasEl = this.canvas.nativeElement;
    const signatureDataUrl = canvasEl.toDataURL('image/png');
    this.formDataService.saveSignature(signatureDataUrl);
    this.clearSignature();
    this.isCanvasHidden = true;
    this.isSaved = true;
    this.isLoaderActive = true;
    this.clientService.is_creation_form_pending = true;
    await this.generateAndSubmitPDF();
  }

  downloadPDF() {
    const doc = this.generatePDF();
    doc.save('agreement.pdf');
  }

  private generatePDF(): jsPDF {
    const doc = new jsPDF();

    const currentLang = this.translate.currentLang || 'en';
  
    if (currentLang === 'ja') {
      doc.addFileToVFS('NotoSansJP-Regular.ttf', notoSansJPRegular);
      doc.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');
      doc.setFont('NotoSansJP');
    } else {
      doc.setFont('sans-serif');
    }

    const t = (key: string, params?: any) => this.translate.instant(`agreementPdf.${key}`, params || {});

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const maxWidth = pageWidth - 2 * margin;
    const lineHeight = currentLang === 'ja' ? 8 : doc.getLineHeight() / doc.internal.scaleFactor;
    let { first_name = 'N/A', last_name = 'N/A', country = 'N/A', state_region = 'N/A', email = 'N/A', phone = 'N/A' } = this.personalInfo;
    const selectedCountry = this.countries.find(
      (c) => c.countryName === country
    );
    country = this.translate.instant(`countries.${selectedCountry?.countryShortCode}`);
    state_region = this.translate.instant(`regions.${selectedCountry?.countryShortCode}.${state_region}`);
    const signature = this.formDataService.getSignature();
    let y = 20;

    const setFont = (style: 'normal' | 'bold') => {
      if (currentLang === 'ja') {
        doc.setFont('NotoSansJP', 'normal');
      } else {
        doc.setFont('sans-serif', style);
      }
    };

    doc.setFontSize(10);
    setFont('bold');
    // doc.text('Date: ', margin, y);
    doc.text(`${t('dateLabel')}: `, margin, y);
    setFont('normal');
    doc.text(this.currentDate, margin + 25, y);
    y += 5;

    setFont('bold');
    // doc.text('Country: ', margin, y);
    doc.text(`${t('countryLabel')}: `, margin, y);
    setFont('normal');
    doc.text(country, margin + 25, y);
    y += 5;

    setFont('bold');
    // doc.text('State/Region: ', margin, y);
    doc.text(`${t('stateRegionLabel')}: `, margin, y);
    setFont('normal');
    doc.text(state_region, margin + 25, y);
    y += 15;

    doc.setFontSize(10);
    setFont('normal');
    // const firstParagraph = `    I, ${first_name} ${last_name} (${email}; ${phone}), a resident of the ${country}, hereby designate GotYourBack Technologies Ltd. as my authorized agent (“Agent”) for the sole purpose of acting on my behalf in connection with the exercise of my data privacy rights under applicable privacy legislation, including but not limited to:`;
    const firstParagraph = t('firstParagraph', { firstName: first_name, lastName: last_name, email, phone, country });;
    const firstParagraphLines = doc.splitTextToSize(firstParagraph, maxWidth);
    doc.text(firstParagraphLines, margin, y);
    y += 15;

    // First list
    // const firstListItems = [
    //   'The California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA);',
    //   'The European Union’s General Data Protection Regulation (GDPR);',
    //   'Canada’s Personal Information Protection and Electronic Documents Act (PIPEDA);',
    //   'The Australian Privacy Act 1988; and',
    //   'Any other applicable data protection or privacy laws in jurisdictions where my personal data may be processed.'
    // ];
    const firstListItems = t('laws') as string[];
    doc.setFontSize(10);
    setFont('normal');
    firstListItems.forEach(item => {
      doc.circle(margin + 10, y - 1, 1, 'F');
      const itemLines = doc.splitTextToSize(item, maxWidth - 20);
      doc.text(itemLines, margin + 15, y);
      y += 5;
    });
    y += 5;

    // const secondParagraph = `    Pursuant to this authorization, GotYourBack Technologies Ltd. is empowered to initiate, manage, and follow through on any and all privacy-related requests, including but not limited to:`;
    const secondParagraph = t('secondParagraph');
    const secondParagraphLines = doc.splitTextToSize(secondParagraph, maxWidth);
    doc.text(secondParagraphLines, margin, y);
    y += 10;

    // const secondListItems = [
    //   'Requesting access to, or the correction, restriction, or deletion of, my personal data;',
    //   'Opting out of data sales, sharing, targeted advertising, profiling, and other processing activities;',
    //   'Withdrawing previously granted consent for the collection or use of my personal data;',
    //   'Facilitating international transfers of personal data as required to complete these actions.'
    // ];
    const secondListItems = t('powers') as string[];
    secondListItems.forEach((item, index) => {
      doc.circle(margin + 10, y - 1, 1, 'F');
      const itemLines = doc.splitTextToSize(item, maxWidth - 20);
      doc.text(itemLines, margin + 15, y);
      const isSpanishSecondItem = currentLang === 'es' && index === 1;
      y += isSpanishSecondItem ? 10 : 5;
    });
    y += 5;

    // const thirdParagraph = `    GotYourBack Technologies Ltd. may communicate directly with third-party data controllers, data brokers, and other relevant entities as necessary to fulfill these requests and may provide any supporting information or documentation required to authenticate or validate the request.`;
    const thirdParagraph = t('communication');;
    const thirdParagraphLines = doc.splitTextToSize(thirdParagraph, maxWidth);
    doc.text(thirdParagraphLines, margin, y);
    y += 15;

    // const fourthParagraph = `    I acknowledge that GotYourBack Technologies Ltd. will make commercially reasonable efforts to execute these requests effectively; however, the Agent cannot guarantee the compliance or cooperation of third-party data recipients, and the outcomes of such requests may be influenced by factors beyond the Agent’s control.`;
    const fourthParagraph = t('efforts');
    const fourthParagraphLines = doc.splitTextToSize(fourthParagraph, maxWidth);
    doc.text(fourthParagraphLines, margin, y);
    y += 15;

    // const fifthParagraph = `    This authorization shall remain in effect for the duration of my active subscription to GotYourBack Technologies Ltd. services and shall automatically terminate upon the termination or expiration of said subscription. I retain the right to revoke this authorization at any time, with written notice to GotYourBack Technologies Ltd.`;
    const fifthParagraph = t('duration');
    const fifthParagraphLines = doc.splitTextToSize(fifthParagraph, maxWidth);
    doc.text(fifthParagraphLines, margin, y);
    y += fifthParagraphLines.length * lineHeight + 12;

    doc.setFontSize(10);
    setFont('bold');
    const signatureText = `${t('signatureLabel')}: `;
    doc.text(signatureText, margin, y);

    const signatureStartX = margin + doc.getTextWidth(signatureText);
    const signatureWidth = 50;
    const signatureHeight = 20;

    if (signature) {
      doc.addImage(signature, 'PNG', signatureStartX, y - 15, signatureWidth, signatureHeight);
    }
    doc.line(signatureStartX, y + 1, signatureStartX + 60, y + 1);

    y += 15;
    const printedNameText = `${t('printedNameLabel')}: ${first_name} ${last_name}`;
    doc.text(printedNameText, margin, y);

    return doc;
  }

  private async generateAndSubmitPDF(): Promise<void> {
    try {
      const doc = this.generatePDF();
      const pdfBlob = doc.output('blob');
  
      const formData = new FormData();
      formData.append('first_name', this.personalInfo.first_name || 'N/A');
      formData.append('last_name', this.personalInfo.last_name || 'N/A');
      formData.append('country', this.personalInfo.country || 'N/A');
      formData.append('state_region', this.personalInfo.state_region || 'N/A');
      formData.append('email', this.personalInfo.email || 'N/A');
      formData.append('phone', this.personalInfo.phone || 'N/A');
      formData.append('agreement_file', pdfBlob, 'agreement.pdf');
      
      const data: any = await firstValueFrom(
        this.clientService.create_authorizat_form(this.auth_client_token, formData)
      );
      
      this.new_form_email = data.email; 
      this.isLoaderActive = false;
      this.clientService.is_creation_form_pending = false;
      this.submit.emit();
    } catch (err) {
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.updateCanvasSize.bind(this));
  }
  switchFormVisibility(event: Event) {
    event.preventDefault();
    this.clientService.set_is_form_visible(false);
    window.location.href = `/dashboard?email=${this.new_form_email}`;
    // window.location.reload();
    // this.router.navigate(["/dashboard"]);
    // this.rerender.emit();
  }
}